/** Cashfree payment gateway — workspace config dialog + per-invoice pay-link section. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Copy, ExternalLink, RefreshCw, Trash2, CheckCircle2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

export function CashfreeConfigButton() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ app_id: "", secret: "", mode: "TEST" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/payments/cashfree/config`).then((r) => setCfg(r.data));
  useEffect(() => { if (open && wsId) load(); /* eslint-disable-next-line */ }, [open, wsId]);

  const save = async () => {
    if (!form.app_id.trim() || !form.secret.trim()) { toast.error("App ID and Secret are required"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/payments/cashfree/config`, form);
      toast.success("Cashfree keys saved (encrypted at rest)");
      setForm({ app_id: "", secret: "", mode: form.mode });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/workspaces/${wsId}/payments/cashfree/config`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-none" onClick={() => setOpen(true)} data-testid="cashfree-config-btn">
        <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Payment gateway
        {cfg?.configured && <CheckCircle2 className="w-3.5 h-3.5 ml-1.5 text-emerald-600" />}
      </Button>
      {open && (
        <Dialog open onOpenChange={(v) => { if (!v) setOpen(false); }}>
          <DialogContent className="max-w-lg" data-testid="cashfree-config-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary" />Cashfree payment gateway</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                {cfg?.configured
                  ? <Badge variant="outline" className="rounded-none border-emerald-500/40 text-emerald-700" data-testid="cashfree-status-badge"><CheckCircle2 className="w-3 h-3 mr-1" />Configured · {cfg.mode} · {cfg.app_id_masked}</Badge>
                  : <Badge variant="outline" className="rounded-none border-amber-500/40 text-amber-700" data-testid="cashfree-status-badge">Not configured</Badge>}
                {cfg?.configured && <Button size="sm" variant="ghost" className="rounded-none text-red-600 h-7" onClick={remove} data-testid="cashfree-delete-btn"><Trash2 className="w-3 h-3" /></Button>}
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger className="h-9 rounded-none" data-testid="cashfree-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TEST">Sandbox (TEST)</SelectItem><SelectItem value="LIVE">Production (LIVE)</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">App ID *</Label>
                <Input value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} placeholder="TEST..." className="h-9 rounded-none font-mono text-xs" data-testid="cashfree-app-id" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1.5 inline-block">Secret key *</Label>
                <Input type="password" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} placeholder="cfsk_..." className="h-9 rounded-none font-mono text-xs" data-testid="cashfree-secret" />
                <p className="text-[11px] text-foreground/55 mt-1">Get sandbox keys at Cashfree Merchant Dashboard → Test mode → Developers → API Keys. Stored encrypted — never shown after save.</p>
              </div>
              {cfg?.webhook_url && (
                <div>
                  <Label className="label-eyebrow mb-1.5 inline-block">Webhook URL (paste in Cashfree → Developers → Webhooks)</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={cfg.webhook_url} className="h-8 rounded-none text-[11px] font-mono" data-testid="cashfree-webhook-url" />
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => copyText(cfg.webhook_url)} data-testid="cashfree-copy-webhook"><Copy className="w-3 h-3" /></Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Close</Button>
              <Button className="rounded-none" onClick={save} disabled={busy} data-testid="cashfree-save-btn">{busy ? "Saving…" : "Save keys"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function InvoicePaySection({ invoice, onChanged }) {
  const [busy, setBusy] = useState("");
  const enabled = !!invoice.online_payment_enabled;

  const toggle = async (v) => {
    setBusy("toggle");
    try {
      await api.patch(`/invoices/${invoice.id}`, { online_payment_enabled: v });
      toast.success(v ? "Online payment enabled" : "Online payment disabled");
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(""); }
  };

  const generate = async () => {
    setBusy("link");
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/payment-link`);
      toast.success(data.reused ? "Existing active link reused" : "Payment link created");
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Link creation failed")); }
    finally { setBusy(""); }
  };

  const checkStatus = async () => {
    setBusy("status");
    try {
      const { data } = await api.get(`/invoices/${invoice.id}/payment-link/status`);
      if (data.payment_recorded) {
        toast.success(`Payment of ${data.recorded_amount} recorded — invoice updated`);
        celebrate("invoice_paid", { doc_no: invoice.doc_no });
      }
      else toast.info(`Link status: ${data.link_status}`);
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Status check failed")); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-border p-4 space-y-3" data-testid="invoice-online-payment">
      <div className="flex items-center gap-3">
        <div className="label-eyebrow flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" />Online payment (Cashfree)</div>
        <Switch checked={enabled} onCheckedChange={toggle} disabled={busy === "toggle"} data-testid="invoice-pay-toggle" />
        {invoice.cashfree_link_status && (
          <Badge variant="outline" className={`rounded-none text-[10px] ml-auto ${invoice.cashfree_link_status === "PAID" ? "border-emerald-500/40 text-emerald-700" : ""}`} data-testid="invoice-paylink-status">
            {invoice.cashfree_link_status}
          </Badge>
        )}
      </div>
      {enabled && (
        <div className="space-y-2">
          {invoice.cashfree_link_url ? (
            <>
              <div className="flex items-center gap-2">
                <Input readOnly value={invoice.cashfree_link_url} className="h-8 rounded-none text-[11px] font-mono" data-testid="invoice-paylink-url" />
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => copyText(invoice.cashfree_link_url)} data-testid="invoice-paylink-copy"><Copy className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => window.open(invoice.cashfree_link_url, "_blank")} data-testid="invoice-paylink-open"><ExternalLink className="w-3 h-3" /></Button>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-none" onClick={checkStatus} disabled={busy === "status"} data-testid="invoice-paylink-check">
                  <RefreshCw className={`w-3 h-3 mr-1 ${busy === "status" ? "animate-spin" : ""}`} />Check payment status
                </Button>
                {invoice.balance_due > 0 && (
                  <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={generate} disabled={busy === "link"} data-testid="invoice-paylink-regenerate">
                    <Link2 className="w-3 h-3 mr-1" />Regenerate for current balance
                  </Button>
                )}
              </div>
            </>
          ) : invoice.balance_due > 0 ? (
            <Button size="sm" className="rounded-none" onClick={generate} disabled={busy === "link"} data-testid="invoice-paylink-generate">
              <Link2 className="w-3 h-3 mr-1" />{busy === "link" ? "Creating…" : "Generate payment link"}
            </Button>
          ) : (
            <p className="text-[11px] text-foreground/55">Invoice fully paid — no link needed.</p>
          )}
          <p className="text-[11px] text-foreground/50">Share the hosted link with your client. Payments auto-record via webhook, or use “Check payment status”.</p>
        </div>
      )}
    </div>
  );
}
